import { useState, useEffect, useCallback } from 'react';
import { 
  ClipboardList,
  CheckCircle, 
  Clock, 
  XCircle,
  PlayCircle,
  ChevronDown,
  ChevronUp,
  Plus,
  Edit3,
  Trash2,
  Check,
  X,
  AlertTriangle,
  Target,
} from 'lucide-react';
import { 
  getTradePlans, 
  getTradePlan, 
  updateTradePlanStatus,
  cancelTradePlan,
  executeTradePlanItem,
} from '../api/client';

function formatCurrency(value) {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function StatusBadge({ status }) {
  const styles = {
    draft: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    approved: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    in_progress: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  const icons = {
    draft: Edit3,
    approved: CheckCircle,
    in_progress: PlayCircle,
    completed: CheckCircle,
    cancelled: XCircle,
  };

  const Icon = icons[status] || Clock;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.draft}`}>
      <Icon className="w-3 h-3" />
      {status.replace('_', ' ')}
    </span>
  );
}

function ItemStatusIcon({ status, fillPct }) {
  if (status === 'complete' || fillPct >= 100) {
    return <CheckCircle className="w-4 h-4 text-emerald-400" />;
  }
  if (status === 'partial' || (fillPct > 0 && fillPct < 100)) {
    return <Target className="w-4 h-4 text-amber-400" />;
  }
  if (status === 'skipped') {
    return <X className="w-4 h-4 text-slate-500" />;
  }
  return <Clock className="w-4 h-4 text-slate-500" />;
}

function ActionBadge({ action }) {
  const styles = {
    BUY: 'bg-emerald-500/20 text-emerald-400',
    SELL: 'bg-red-500/20 text-red-400',
    TRIM: 'bg-amber-500/20 text-amber-400',
  };
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[action] || 'bg-slate-500/20 text-slate-400'}`}>
      {action}
    </span>
  );
}

function ProgressBar({ percentage }) {
  const color = percentage >= 100 ? 'bg-emerald-500' : percentage > 0 ? 'bg-amber-500' : 'bg-slate-600';
  return (
    <div className="w-full bg-slate-700 rounded-full h-2">
      <div 
        className={`${color} h-2 rounded-full transition-all`}
        style={{ width: `${Math.min(100, percentage)}%` }}
      />
    </div>
  );
}

function ExecuteModal({ item, onClose, onExecute }) {
  const [shares, setShares] = useState('');
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!shares || !price) return;
    
    setSubmitting(true);
    try {
      await onExecute(item.id, parseFloat(shares), parseFloat(price), notes || null);
      onClose();
    } catch (err) {
      console.error('Error executing item:', err);
      alert('Failed to record execution: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = (item.target_shares || 0) - (item.executed_shares || 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-slate-200 mb-4">
          Record Execution: {item.action} {item.ticker}
        </h3>
        
        <div className="text-sm text-slate-400 mb-4">
          Target: {item.target_shares?.toFixed(2) || item.target_dollars?.toFixed(0)} 
          {item.target_shares ? ' shares' : ' dollars'}
          {remaining > 0 && <span className="ml-2 text-amber-400">({remaining.toFixed(2)} remaining)</span>}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Shares Executed</label>
            <input
              type="number"
              step="0.001"
              value={shares}
              onChange={e => setShares(e.target.value)}
              placeholder={remaining > 0 ? remaining.toFixed(3) : '0'}
              className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-slate-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Price per Share</label>
            <input
              type="number"
              step="0.01"
              value={price}
              onChange={e => setPrice(e.target.value)}
              placeholder="0.00"
              className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-slate-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Order ID, broker notes, etc."
              className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-slate-200"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded hover:bg-slate-600 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !shares || !price}
              className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-500 transition disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Record'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CancelModal({ plan, onClose, onCancel }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;
    
    setSubmitting(true);
    try {
      await onCancel(plan.id, reason);
      onClose();
    } catch (err) {
      console.error('Error cancelling plan:', err);
      alert('Failed to cancel plan: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-slate-200 mb-2">
          Cancel Plan
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          This will cancel "{plan.name}". A reason is required for future reference.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Cancellation Reason *</label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Why is this plan being cancelled?"
              className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-slate-200 h-24"
              required
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded hover:bg-slate-600 transition"
            >
              Keep Plan
            </button>
            <button
              type="submit"
              disabled={submitting || !reason.trim()}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-500 transition disabled:opacity-50"
            >
              {submitting ? 'Cancelling...' : 'Cancel Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PlanCard({ plan: initialPlan, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [plan, setPlan] = useState(initialPlan);
  const [loading, setLoading] = useState(false);
  const [executeItem, setExecuteItem] = useState(null);
  const [showCancel, setShowCancel] = useState(false);

  const loadDetails = async () => {
    if (plan.items && plan.items.length > 0) {
      setExpanded(!expanded);
      return;
    }
    
    setLoading(true);
    try {
      const details = await getTradePlan(plan.id);
      setPlan(details);
      setExpanded(true);
    } catch (err) {
      console.error('Error loading plan details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      const updated = await updateTradePlanStatus(plan.id, 'approved');
      setPlan(updated);
      onRefresh?.();
    } catch (err) {
      console.error('Error approving plan:', err);
      alert('Failed to approve plan: ' + err.message);
    }
  };

  const handleComplete = async () => {
    if (!confirm('Mark this plan as completed?')) return;
    try {
      const updated = await updateTradePlanStatus(plan.id, 'completed');
      setPlan(updated);
      onRefresh?.();
    } catch (err) {
      console.error('Error completing plan:', err);
      alert('Failed to complete plan: ' + err.message);
    }
  };

  const handleExecuteItem = async (itemId, shares, price, notes) => {
    const updated = await executeTradePlanItem(itemId, shares, price, notes);
    // Reload full plan to get updated totals
    const refreshed = await getTradePlan(plan.id);
    setPlan(refreshed);
    onRefresh?.();
  };

  const handleCancel = async (planId, reason) => {
    const updated = await cancelTradePlan(planId, reason);
    setPlan(updated);
    onRefresh?.();
  };

  const completedItems = plan.items?.filter(i => i.item_status === 'complete').length || plan.executed_items || 0;
  const totalItems = plan.items?.length || plan.total_items || 0;
  const fillPct = plan.overall_fill_percentage || (totalItems > 0 ? completedItems / totalItems * 100 : 0);

  const isActive = ['draft', 'approved', 'in_progress'].includes(plan.status);

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-slate-700/30 transition"
        onClick={loadDetails}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <ClipboardList className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <h3 className="font-medium text-slate-200 truncate">{plan.name}</h3>
              <StatusBadge status={plan.status} />
            </div>
            
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <span>Created: {formatDate(plan.created_at)}</span>
              <span>{completedItems}/{totalItems} items</span>
              {plan.total_executed_dollars > 0 && (
                <span>{formatCurrency(plan.total_executed_dollars)} deployed</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Progress indicator */}
            <div className="w-24">
              <div className="text-xs text-slate-400 text-right mb-1">{fillPct.toFixed(0)}%</div>
              <ProgressBar percentage={fillPct} />
            </div>
            
            {loading ? (
              <div className="w-5 h-5 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && plan.items && (
        <div className="border-t border-slate-700/50">
          {/* Plan ID & Rationale */}
          <div className="px-4 py-3 bg-slate-700/30 text-sm">
            <div className="flex items-center gap-4 mb-2">
              <span className="text-slate-400">
                Plan ID: <code className="bg-slate-800 px-2 py-0.5 rounded text-amber-400 font-mono">{plan.id}</code>
              </span>
              <span className="text-slate-500 text-xs">
                (use this ID when prompting Claude)
              </span>
            </div>
            {plan.rationale && (
              <div className="text-slate-300">
                <strong>Rationale:</strong> {plan.rationale}
              </div>
            )}
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-700/50 text-slate-400">
                <tr>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Ticker</th>
                  <th className="px-4 py-2 text-left">Account</th>
                  <th className="px-4 py-2 text-center">Action</th>
                  <th className="px-4 py-2 text-right">Target</th>
                  <th className="px-4 py-2 text-right">Executed</th>
                  <th className="px-4 py-2 text-right">Fill %</th>
                  {isActive && <th className="px-4 py-2 text-center">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {plan.items.map(item => (
                  <tr key={item.id} className="border-t border-slate-700/30 hover:bg-slate-700/20">
                    <td className="px-4 py-2">
                      <ItemStatusIcon status={item.item_status} fillPct={item.fill_percentage} />
                    </td>
                    <td className="px-4 py-2 font-medium text-slate-200">{item.ticker}</td>
                    <td className="px-4 py-2 text-slate-400 text-xs">{item.account_id}</td>
                    <td className="px-4 py-2 text-center">
                      <ActionBadge action={item.action} />
                    </td>
                    <td className="px-4 py-2 text-right text-slate-300">
                      {item.target_shares ? `${item.target_shares.toFixed(2)} sh` : formatCurrency(item.target_dollars)}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-300">
                      {item.executed_shares > 0 ? (
                        <span>
                          {item.executed_shares.toFixed(2)} @ ${item.executed_price?.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span className={item.fill_percentage >= 100 ? 'text-emerald-400' : item.fill_percentage > 0 ? 'text-amber-400' : 'text-slate-500'}>
                        {item.fill_percentage?.toFixed(0) || 0}%
                      </span>
                    </td>
                    {isActive && (
                      <td className="px-4 py-2 text-center">
                        {item.item_status !== 'complete' && item.item_status !== 'skipped' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setExecuteItem(item); }}
                            className="px-2 py-1 text-xs bg-emerald-600/20 text-emerald-400 rounded hover:bg-emerald-600/30 transition"
                          >
                            Execute
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Action Buttons */}
          {isActive && (
            <div className="px-4 py-3 border-t border-slate-700/50 flex gap-3">
              {plan.status === 'draft' && (
                <button
                  onClick={handleApprove}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-500 transition"
                >
                  Approve Plan
                </button>
              )}
              {plan.status === 'in_progress' && (
                <button
                  onClick={handleComplete}
                  className="px-4 py-2 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-500 transition"
                >
                  Complete Plan
                </button>
              )}
              <button
                onClick={() => setShowCancel(true)}
                className="px-4 py-2 bg-slate-700 text-slate-300 text-sm rounded hover:bg-slate-600 transition"
              >
                Cancel Plan
              </button>
            </div>
          )}

          {/* Cancellation info */}
          {plan.status === 'cancelled' && plan.cancellation_reason && (
            <div className="px-4 py-3 border-t border-slate-700/50 bg-red-500/10">
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-red-400 font-medium">Cancelled: {formatDate(plan.cancelled_at)}</div>
                  <div className="text-slate-400 mt-1">{plan.cancellation_reason}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {executeItem && (
        <ExecuteModal 
          item={executeItem} 
          onClose={() => setExecuteItem(null)} 
          onExecute={handleExecuteItem}
        />
      )}
      {showCancel && (
        <CancelModal
          plan={plan}
          onClose={() => setShowCancel(false)}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
}

export default function TradePlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active'); // 'active', 'all', 'completed', 'cancelled'

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let status = null;
      let limit = 50;
      
      if (filter === 'active') {
        // Get draft, approved, in_progress
        const result = await getTradePlans(null, true, limit);
        const activePlans = result.plans?.filter(p => 
          ['draft', 'approved', 'in_progress'].includes(p.status)
        ) || [];
        setPlans(activePlans);
        setLoading(false);
        return;
      } else if (filter === 'completed') {
        status = 'completed';
      } else if (filter === 'cancelled') {
        status = 'cancelled';
      }
      
      const result = await getTradePlans(status, true, limit);
      setPlans(result.plans || []);
    } catch (err) {
      console.error('Error loading trade plans:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const activePlans = plans.filter(p => ['draft', 'approved', 'in_progress'].includes(p.status));
  const hasActivePlans = activePlans.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-200 flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-amber-400" />
          Trade Plans
        </h2>
        
        {/* Filter Tabs */}
        <div className="flex gap-1 bg-slate-800 rounded-lg p-1">
          {[
            { id: 'active', label: 'Active' },
            { id: 'completed', label: 'Completed' },
            { id: 'cancelled', label: 'Cancelled' },
            { id: 'all', label: 'All' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-3 py-1.5 text-sm rounded-md transition ${
                filter === tab.id
                  ? 'bg-slate-700 text-slate-200'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400">
          Error loading plans: {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!loading && plans.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No {filter === 'all' ? '' : filter} trade plans found.</p>
          <p className="text-sm mt-2">
            Create a trade plan via Claude Desktop to track your rebalancing.
          </p>
        </div>
      )}

      {/* Plans List */}
      {!loading && plans.length > 0 && (
        <div className="space-y-4">
          {plans.map(plan => (
            <PlanCard key={plan.id} plan={plan} onRefresh={loadPlans} />
          ))}
        </div>
      )}
    </div>
  );
}

